import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { styled } from '@shared/ui';
import { selectBreadcrumbsResolved } from 'redux_store/selectors/navigationSelectors';

const Trail = styled('nav')({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 13,
  color: 'var(--qhs-text-2)',
  minWidth: 0,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const CrumbLink = styled('button')({
  background: 'transparent',
  border: 'none',
  padding: 0,
  font: 'inherit',
  color: 'var(--qhs-text-2)',
  cursor: 'pointer',
  maxWidth: 200,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  '&:hover': { color: 'var(--qhs-brand)' },
});

const CrumbCurrent = styled('span')({
  color: 'var(--qhs-text-1)',
  fontWeight: 600,
  maxWidth: 200,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const Sep = styled('span')({ color: 'var(--qhs-text-4)' });

const Ellipsis = styled('span')({
  color: 'var(--qhs-text-3)',
  cursor: 'help',
});

// Skeleton placeholder while a dynamic label (e.g. patient name) is
// loading. Visible bar with a moving highlight, sized to roughly match a
// typical name. Non-interactive — clicking a no-name entry would be
// confusing UX, so we wait for the label before exposing navigation.
const CrumbSkeleton = styled('span')({
  display: 'inline-block',
  width: 96,
  height: 12,
  borderRadius: 4,
  background:
    'linear-gradient(90deg, var(--qhs-surface-hover) 0%, var(--qhs-border) 50%, var(--qhs-surface-hover) 100%)',
  backgroundSize: '160px 100%',
  backgroundRepeat: 'no-repeat',
  animation: 'qhs-breadcrumb-shimmer 1.4s ease-in-out infinite',
  verticalAlign: 'middle',
  '@keyframes qhs-breadcrumb-shimmer': {
    '0%': { backgroundPosition: '-160px 0' },
    '100%': { backgroundPosition: 'calc(160px + 100%) 0' },
  },
});

/**
 * Collapse the stack for display.
 *   <= 4 entries — show all
 *   >= 5 entries — first + ellipsis + last three
 */
const buildVisible = stack => {
  if (stack.length <= 4) return { head: [stack[0]], hidden: [], tail: stack.slice(1) };
  return {
    head: [stack[0]],
    hidden: stack.slice(1, stack.length - 3),
    tail: stack.slice(stack.length - 3),
  };
};

const Breadcrumbs = () => {
  const navigate = useNavigate();
  const stack = useSelector(selectBreadcrumbsResolved);

  const visible = useMemo(() => buildVisible(stack), [stack]);

  if (stack.length === 0) return null;
  if (stack.length === 1) {
    return (
      <Trail aria-label="breadcrumb">
        {stack[0].label ? (
          <CrumbCurrent>{stack[0].label}</CrumbCurrent>
        ) : (
          <CrumbSkeleton aria-label="loading" />
        )}
      </Trail>
    );
  }

  const renderEntry = (entry, isLast) => {
    if (!entry.label) {
      return <CrumbSkeleton key={entry.resolvedPath} aria-label="loading" />;
    }
    return isLast ? (
      <CrumbCurrent key={entry.resolvedPath} title={entry.label}>
        {entry.label}
      </CrumbCurrent>
    ) : (
      <CrumbLink
        key={entry.resolvedPath}
        type="button"
        title={entry.label}
        onClick={() => navigate(entry.resolvedPath)}
      >
        {entry.label}
      </CrumbLink>
    );
  };

  const allTail = visible.tail;
  const lastIdx = allTail.length - 1;

  return (
    <Trail aria-label="breadcrumb">
      {visible.head.map(e => renderEntry(e, false))}
      {visible.hidden.length > 0 && (
        <>
          <Sep>/</Sep>
          <Ellipsis title={visible.hidden.map(e => e.label || '…').join(' / ')}>…</Ellipsis>
        </>
      )}
      {allTail.map((e, idx) => (
        <span
          key={e.resolvedPath}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <Sep>/</Sep>
          {renderEntry(e, idx === lastIdx)}
        </span>
      ))}
    </Trail>
  );
};

export default Breadcrumbs;
